import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import blogFetch from "../../axios/config";
import Navbar from "../../layout/Navbar/Navbar";
import Sidebar from "../../layout/Sidebar/SideBar";
import QRCodeReaderOrdem from "../Apontamento/QRCodeReaderOrdem";

// Rotas do backend (as mesmas usadas na verificação de QR da serigrafia).
const ROTA_PECA = "ISAGA_ApontamentoSerigrafia/getPeca";
const ROTA_VERIFICAR_LINK = "ISAGA_ApontamentoSerigrafia/verificar-link";
const ROTA_VALIDAR_MATRICULA = "IAcompanhamentoMontagem/validate-matricula";

const DOMINIO_ESPERADO = "q.agpbrasil.com.br";
const STORAGE_MATRICULA = "listaMontagem_matriculas_SAP";
const TOAST = { duration: 6000 };

// Último segmento da URL da ficha (é o lote).
const ultimoSegmento = (url) =>
  url.trim().replace(/\/+$/, "").split("/").filter(Boolean).pop() || "";

// Interpreta a resposta do backend como aprovado/reprovado.
const interpretarBackOk = (data) => {
  if (data && typeof data === "object") {
    if ("aprovado" in data) return Boolean(data.aprovado);
    if ("ok" in data || "valido" in data) return Boolean(data.ok ?? data.valido);
    if (typeof data.resultado === "string") return data.resultado.trim().toUpperCase() === "APROVADO";
    return Boolean(data.existe) && Boolean(data.funcional) && Boolean(data.contemLote);
  }
  return Boolean(data);
};

// Detecta se o valor lido é o QR da ficha (link) ou o da peça (ordem).
const pareceLink = (valor) => {
  const v = (valor || "").toLowerCase();
  return v.startsWith("http") || v.includes(DOMINIO_ESPERADO);
};

const isTeclaConfirma = (e) =>
  e.key === "Enter" || e.key === "NumpadEnter" || e.key === "Tab";

export default function VerificacaoQR() {
  // Peça e ficha (ordem livre — cada um cai no lugar certo pelo conteúdo)
  const [dadosPeca, setDadosPeca] = useState(null);
  const [ordemInput, setOrdemInput] = useState("");
  const [linkLido, setLinkLido] = useState("");
  const [ordemQR, setOrdemQR] = useState(null);
  const [carregandoOrdemQR, setCarregandoOrdemQR] = useState(false);
  const [verificacao, setVerificacao] = useState(null);
  const [carregando, setCarregando] = useState(false);

  // Identificação do operador (matrícula SAP)
  const [matricula, setMatricula] = useState("");
  const [matriculaSAP, setMatriculaSAP] = useState("");
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [isMatriculaModalOpen, setIsMatriculaModalOpen] = useState(false);
  const [matriculaTemp, setMatriculaTemp] = useState("");
  const [validandoMatricula, setValidandoMatricula] = useState(false);

  // ----- Matrícula SAP -----
  const validarMatriculaSAP = useCallback(async (valorMatricula) => {
    const valor = valorMatricula.trim();
    if (!/^\d{6}$/.test(valor)) return null;
    try {
      const { data } = await blogFetch.get(`${ROTA_VALIDAR_MATRICULA}/${encodeURIComponent(valor)}`);
      if (!data?.exists) return null;
      return { nome: data.nome ?? "", matriculaSAP: data.matricula_SAP ?? "" };
    } catch {
      return null;
    }
  }, []);

  const salvarMatriculaStorage = (valor, nome, matriculaSap) => {
    localStorage.setItem(
      STORAGE_MATRICULA,
      JSON.stringify({ principal: valor, nome, matriculaSAP: matriculaSap })
    );
  };

  const limparMatricula = () => {
    localStorage.removeItem(STORAGE_MATRICULA);
    setMatricula("");
    setNomeUsuario("");
    setMatriculaSAP("");
  };

  useEffect(() => {
    const armazenado = localStorage.getItem(STORAGE_MATRICULA);
    if (!armazenado) return;

    let valor = "";
    let nomeSalvo = "";
    let matriculaSAPSalva = "";
    try {
      const obj = JSON.parse(armazenado);
      valor = obj.principal || obj.secundaria || "";
      nomeSalvo = obj.nome || "";
      matriculaSAPSalva = obj.matriculaSAP || "";
    } catch {
      valor = armazenado;
    }
    if (!valor) return;

    setMatricula(valor);
    setNomeUsuario(nomeSalvo);
    setMatriculaSAP(matriculaSAPSalva);

    (async () => {
      const resultado = await validarMatriculaSAP(valor);
      if (resultado === null) {
        limparMatricula();
        toast.error("Matrícula não encontrada no SAP");
      } else {
        setNomeUsuario(resultado.nome);
        setMatriculaSAP(resultado.matriculaSAP);
        salvarMatriculaStorage(valor, resultado.nome, resultado.matriculaSAP);
      }
    })();
  }, [validarMatriculaSAP]);

  const handleMatriculaChange = (valor) => setMatriculaTemp(valor.replace(/\D/g, "").slice(0, 6));

  const handleSalvarMatricula = async () => {
    const valor = matriculaTemp.trim();
    if (!/^\d{6}$/.test(valor)) {
      toast.error("A matrícula SAP deve ter 6 dígitos numéricos.");
      return;
    }
    setValidandoMatricula(true);
    const resultado = await validarMatriculaSAP(valor);
    setValidandoMatricula(false);
    if (resultado === null) {
      toast.error("Matrícula não encontrada no SAP");
      return;
    }
    salvarMatriculaStorage(valor, resultado.nome, resultado.matriculaSAP);
    setMatricula(valor);
    setNomeUsuario(resultado.nome);
    setMatriculaSAP(resultado.matriculaSAP);
    setMatriculaTemp("");
    setIsMatriculaModalOpen(false);
    toast.success(`Matrícula registrada: ${resultado.nome}`);
  };

  // ----- Reset -----
  const resetarLeitura = () => {
    setDadosPeca(null);
    setOrdemInput("");
    setLinkLido("");
    setOrdemQR(null);
    setCarregandoOrdemQR(false);
    setVerificacao(null);
    setCarregando(false);
  };

  const relerPeca = () => {
    setDadosPeca(null);
    setOrdemInput("");
    setVerificacao(null);
  };

  const relerLink = () => {
    setLinkLido("");
    setOrdemQR(null);
    setCarregandoOrdemQR(false);
    setVerificacao(null);
  };

  // ----- Busca da peça (obtém o lote esperado) -----
  const buscarPeca = async (ordem) => {
    const valor = ordem?.trim();
    if (!valor) return;
    setCarregando(true);
    setVerificacao(null);
    try {
      const { data } = await blogFetch.get(ROTA_PECA, { params: { ordem: valor } });
      if (!data || !data.ordem_Nro_Lote) {
        toast.error("Peça encontrada, mas sem lote retornado pelo servidor.", TOAST);
      }
      setDadosPeca(data);
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.status === 404
          ? "Peça não encontrada para o QR lido."
          : error?.response?.data?.message || "Erro ao buscar a peça no servidor.";
      toast.error(msg, TOAST);
    } finally {
      setCarregando(false);
    }
  };

  // ----- Ordem do QR da peça (o lote lido no QR vira ordem no backend) -----
  const buscarOrdemDoQR = async (url) => {
    const lote = ultimoSegmento(url);
    if (!lote) return;
    setCarregandoOrdemQR(true);
    setOrdemQR(null);
    try {
      const { data } = await blogFetch.get(ROTA_PECA, { params: { lote } });
      setOrdemQR(data?.ordem_Serial || "");
    } catch (error) {
      console.error(error);
      setOrdemQR("");
    } finally {
      setCarregandoOrdemQR(false);
    }
  };

  // ----- Verificação no backend (fonte de verdade) -----
  const verificarBackend = useCallback(
    async (url) => {
      if (!url || !dadosPeca?.ordem_Nro_Lote) return;
      const loteEsperado = String(dadosPeca.ordem_Nro_Lote).trim();
      const loteLido = ultimoSegmento(url);
      const loteConfere = loteLido === loteEsperado;

      setCarregando(true);
      try {
        const { data } = await blogFetch.get(ROTA_VERIFICAR_LINK, {
          params: { url, lote: loteEsperado, matricula: matriculaSAP || matricula || "" },
          timeout: 6000,
        });
        const aprovado = interpretarBackOk(data);
        setVerificacao({
          aprovado,
          loteEsperado,
          loteLido,
          loteConfere,
          urlLida: url,
          mensagem: data?.mensagem || data?.message || "",
        });
        if (aprovado) {
          toast.success(data?.mensagem || "QR verificado com sucesso! Pronto para a próxima peça.", TOAST);
          setTimeout(resetarLeitura, 2200);
        } else {
          toast.error(data?.mensagem || data?.message || "QR reprovado na verificação.", TOAST);
        }
      } catch (error) {
        console.error(error);
        setVerificacao({
          aprovado: false,
          loteEsperado,
          loteLido,
          loteConfere,
          urlLida: url,
          mensagem: "Erro de comunicação com o servidor.",
        });
        toast.error("Erro ao verificar o QR no servidor.", TOAST);
      } finally {
        setCarregando(false);
      }
    },
    [dadosPeca, matricula, matriculaSAP]
  );

  // Dispatcher "ler indiferente": qualquer leitura/digitação cai no campo certo.
  const handleLeitura = (valorBruto) => {
    const valor = (valorBruto || "").trim();
    if (!valor) return;
    if (pareceLink(valor)) {
      setLinkLido(valor);
      buscarOrdemDoQR(valor);
    } else {
      setOrdemInput(valor);
      buscarPeca(valor);
    }
  };

  // Quando peça (com lote) e link estiverem presentes, verifica no backend.
  useEffect(() => {
    if (dadosPeca?.ordem_Nro_Lote && linkLido && !verificacao && !carregando) {
      verificarBackend(linkLido);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dadosPeca, linkLido]);

  const camposPeca = dadosPeca
    ? [
        ["Ordem", dadosPeca.ordem_Serial],
        ["Lote", dadosPeca.ordem_Nro_Lote],
        ["Descrição do Veículo", dadosPeca.ordem_VehDesc],
        ["Sub Produto", dadosPeca.subProduto_Desc],
        ["Set Atual", dadosPeca.ordem_SetAtual],
      ]
    : [];

  return (
    <div className="container_verificacaoQR">
      <Navbar />
      <Sidebar />

      <div className="app_body">
        <div
          className="mx-auto px-3 py-4"
          style={{ maxWidth: "680px", width: "100%" }}
        >
          <h1 className="fw-bold fs-2 text-center mb-4">Verificação de QR - Serigrafia/Vitrificado</h1>

          {/* Operador */}
          <div className="d-flex align-items-center justify-content-between mb-3 p-3 bg-light border rounded">
            <span className="fs-5">
              <span className="text-muted">Operador: </span>
              {nomeUsuario ? (
                <span className="fw-bold">{nomeUsuario} ({matricula})</span>
              ) : (
                <span className="text-muted fst-italic">não identificado</span>
              )}
            </span>
            <Button
              variant={matricula ? "outline-secondary" : "warning"}
              size="lg"
              onClick={() => {
                setMatriculaTemp(matricula);
                setIsMatriculaModalOpen(true);
              }}
            >
              {matricula ? "Trocar" : "Registrar"}
            </Button>
          </div>

          {carregando && (
            <div className="text-center my-3 fs-5">
              <Spinner animation="border" className="me-2" />
              Processando...
            </div>
          )}

          {/* Campo PEÇA */}
          <div className="mb-3">
            <h5 className="fw-bold text-secondary mb-2">1 · Ficha (ordem)</h5>
            {!dadosPeca ? (
              <>
                <input
                  type="text"
                  className="form-control form-control-lg mb-2"
                  placeholder="Leia ou digite a ordem da ficha"
                  value={ordemInput}
                  autoFocus
                  autoComplete="off"
                  disabled={carregando}
                  onChange={(e) => setOrdemInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (isTeclaConfirma(e)) {
                      e.preventDefault();
                      handleLeitura(ordemInput);
                    }
                  }}
                />
                <QRCodeReaderOrdem onLeitura={handleLeitura} />
              </>
            ) : (
              <div className="border rounded p-3 bg-light">
                <ul className="list-unstyled mb-3">
                  {camposPeca.map(([chave, valor]) => (
                    <li key={chave} className="d-flex justify-content-between border-top py-2 fs-5">
                      <span className="text-muted">{chave}</span>
                      <span className="fw-semibold text-end">{String(valor ?? "")}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="w-100"
                  onClick={relerPeca}
                  disabled={carregando}
                >
                  Escanear ficha novamente
                </Button>
              </div>
            )}
          </div>

          {/* Campo FICHA */}
          <div className="mb-3">
            <h5 className="fw-bold text-secondary mb-2">2 · QR da peça</h5>
            {!linkLido ? (
              <>
                <p className="text-muted fs-5 mb-2">Escaneie o QR Code da peça</p>
                <QRCodeReaderOrdem onLeitura={handleLeitura} />
              </>
            ) : (
              <div className="border rounded p-3 bg-light">
                <div className="d-flex justify-content-between align-items-center fs-5">
                  <span className="text-muted">Ordem</span>
                  {carregandoOrdemQR ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <span className="fw-semibold text-end">{ordemQR || "—"}</span>
                  )}
                </div>
                <Button
                  variant="outline-primary"
                  size="lg"
                  className="w-100 mt-3"
                  onClick={relerLink}
                  disabled={carregando}
                >
                  Escanear peça novamente
                </Button>
              </div>
            )}
          </div>

          {/* Resultado */}
          {verificacao && (
            <div
              className={`border rounded p-3 mb-3 ${
                verificacao.aprovado
                  ? "border-success bg-success bg-opacity-10"
                  : "border-danger bg-danger bg-opacity-10"
              }`}
            >
              <div
                className={`fw-bold text-center mb-3 ${verificacao.aprovado ? "text-success" : "text-danger"}`}
                style={{ fontSize: "2.4rem" }}
              >
                {verificacao.aprovado ? "✓ QR APROVADO" : "✗ QR REPROVADO"}
              </div>
              {verificacao.mensagem && (
                <div className="text-center fs-5 mb-3">{verificacao.mensagem}</div>
              )}
              <div className="fs-5">
                <div>
                  <span className="text-muted">Lote esperado: </span>
                  <span className="fw-bold text-success">{verificacao.loteEsperado}</span>
                </div>
                <div>
                  <span className="text-muted">Lote do link: </span>
                  <span className={`fw-bold ${verificacao.loteConfere ? "text-success" : "text-danger"}`}>
                    {verificacao.loteLido || "—"}
                  </span>
                </div>
                <div className="mt-2 text-break">
                  <span className="text-muted">Link: </span>
                  {verificacao.urlLida}
                </div>
              </div>
            </div>
          )}

          {(dadosPeca || linkLido || verificacao) && (
            <Button
              variant="outline-secondary"
              size="lg"
              className="w-100"
              onClick={resetarLeitura}
              disabled={carregando}
            >
              Limpar tudo
            </Button>
          )}
        </div>
      </div>

      {/* Modal de registro de matrícula */}
      <Modal show={isMatriculaModalOpen} onHide={() => setIsMatriculaModalOpen(false)} centered>
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="fw-bold fs-4">Registrar Matrícula SAP</Modal.Title>
        </Modal.Header>
        <Modal.Body className="py-4">
          <label className="form-label fw-semibold text-muted">MATRÍCULA SAP (6 dígitos)</label>
          <input
            type="text"
            className="form-control form-control-lg text-primary fw-bold text-center"
            placeholder="Ex: 123456"
            value={matriculaTemp}
            autoFocus
            maxLength={6}
            onChange={(e) => handleMatriculaChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSalvarMatricula();
            }}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="lg" onClick={() => setIsMatriculaModalOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSalvarMatricula}
            disabled={matriculaTemp.length !== 6 || validandoMatricula}
          >
            {validandoMatricula ? "Validando..." : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
